import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../../config/firebase';
import { authService } from '../services/authService';
import { userService } from '../services/userService';
import { useAuthStore } from '../store/authStore';

// Map Firebase error codes to user-friendly messages
const getErrorMessage = (error) => {
  // If it's an axios error with a response from our backend
  if (error.response?.data?.error) {
    // Check for email verification errors
    if (error.response.data.requiresVerification) {
      return 'Email verification required. Please verify your email before signing in.';
    }
    return error.response.data.error;
  }

  // Firebase error codes
  const errorMessages = {
    'auth/invalid-credential': 'Invalid email or password. Please check your credentials.',
    'auth/user-not-found': 'No account exists with this email. Would you like to sign up?',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/email-already-in-use': 'An account already exists with this email. Would you like to sign in?',
    'auth/weak-password': 'Password should be at least 6 characters long.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/operation-not-allowed': 'This sign-in method is not enabled.',
    'auth/popup-closed-by-user': 'Sign-in window was closed. Please try again.',
    'auth/cancelled-popup-request': 'The process was cancelled. Please try again.',
    'auth/popup-blocked': 'Pop-up was blocked by the browser. Please allow pop-ups for this site.',
    'auth/network-request-failed': 'Network error. Please check your internet connection.',
    'auth/email-not-verified': 'Email verification required. Please verify your email before signing in.',
    'default': 'An error occurred during authentication. Please try again.'
  };

  return error.code ? errorMessages[error.code] || errorMessages.default : errorMessages.default;
};

export const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uid, setUid] = useState(null);
  const navigate = useNavigate();
  const { setAuth, clearAuth } = useAuthStore();

  const handleAuthSuccess = async (firebaseUser, token) => {
    try {
      // Set token for API calls if not provided
      if (!token) {
        token = await authService.getToken();
      }

      // Set token for API calls
      userService.setAuthToken(token);

      // Get user data from backend
      const userData = await userService.validateToken();

      // Update store with both token and user data
      setAuth(token, userData);

      // Start token refresh process
      authService.startTokenRefresh();

      // Navigate to home
      navigate('/');
    } catch (error) {
      console.error('Backend sync error:', error);
      throw error; // Re-throw to be handled by the caller
    }
  };

  const handleAuthError = (error) => {
    console.error('Auth Error:', error);
    setError(getErrorMessage(error));
    setUid(error?.response?.data?.uid || null);
    setLoading(false);
    authService.stopTokenRefresh(); // Stop token refresh
    clearAuth(); // Clear auth state on error
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      const user = await authService.loginWithEmail(email, password);
      await handleAuthSuccess(user);
    } catch (error) {
      handleAuthError(error);
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      setError(null);
      const user = await authService.loginWithGoogle();

      // Get token for API calls
      const token = await authService.getToken();
      userService.setAuthToken(token);

      try {
        // Try to validate the token (check if user exists in backend)
        await handleAuthSuccess(user, token);
      } catch (backendError) {
        console.error('Backend validation error:', backendError);
        // If error is because user doesn't exist in backend, register them
        if (backendError.response?.status === 404 ||
          backendError.response?.data?.error?.includes('not found') ||
          backendError.message?.includes('not found')) {
          console.log('User not found in backend, registering new user');

          // Register with backend using Google user data
          await userService.register({
            uid: user.uid,
            email: user.email,
            name: user.displayName || user.email.split('@')[0]
          });

          // Complete authentication flow after registration
          await handleAuthSuccess(user, token);
        } else {
          // If it's another type of error, rethrow it
          throw backendError;
        }
      }
    } catch (error) {
      handleAuthError(error);
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, name) => {
    try {
      setLoading(true);
      setError(null);

      // 1. Register with Firebase Auth
      const user = await authService.registerWithEmail(email, password);
      console.log('Firebase Auth registration successful:', user);

      // 2. Update Firebase profile
      await authService.updateProfile(name);
      console.log('Firebase profile updated');

      // 3. Get Firebase token
      const token = await authService.getToken();
      console.log('Got Firebase token');

      // 4. Set token for API calls
      userService.setAuthToken(token);

      // 5. Register with backend
      const response = await userService.register({
        uid: user.uid,
        email: user.email,
        name
      });
      console.log('Backend registration successful');

      // 6. Check if email verification is required
      if (response.requiresVerification) {
        // Redirect to email verification page
        navigate(`/verify-email?uid=${user.uid}`);
        return;
      }

      // 7. Complete authentication flow if no verification required
      await handleAuthSuccess(user, token);
      console.log('Authentication flow completed');
    } catch (error) {
      console.error('Registration error:', error);
      handleAuthError(error);

      // If Firebase registration succeeded but backend failed, try to clean up
      if (auth.currentUser) {
        try {
          await auth.currentUser.delete();
          console.log('Cleaned up Firebase user after backend registration failed');
        } catch (deleteError) {
          console.error('Error cleaning up Firebase user:', deleteError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.handleLogout();
      clearAuth();
      userService.setAuthToken(null);
      navigate('/signin');
    } catch (error) {
      handleAuthError(error);
    }
  };

  return {
    login,
    loginWithGoogle,
    register,
    logout,
    loading,
    error,
    uid,
    clearError: () => setError(null)
  };
};
